import { useQuery } from '@tanstack/react-query'

import { consulta, pedir } from './nucleo'
import type { Sitio } from './tipos'

/**
 * Los dominios de una web (CLAUDE.md §4, «Conectar un dominio propio»): lo que responde
 * `GET /api/panel/dominios`. El estado lo decide el núcleo, que es quien mira el DNS y
 * Cloudflare; aquí solo se pinta.
 */
export type EstadoDeDominio = 'certificado' | 'conectado' | 'conectando' | 'esperando-dns' | 'problema'

export type DominioDeLaWeb = {
  dominio: string
  principal: boolean
  raiz: boolean
  estado: EstadoDeDominio
  problema: null | string
  comprobadoEn: null | string
  registro: { tipo: 'CNAME'; nombre: string; valor: string }
  www: null | string
}

export type DominiosDeLaWeb = {
  sitio: number
  direccionFija: string
  destino: string
  automatico: boolean
  /** Si las demás direcciones llevan a la principal con un 301, y si ya lo hacen (la principal tiene que funcionar). Sin el dato, un núcleo anterior. */
  redirigir?: { activo: boolean; enMarcha: boolean; principal: string }
  dominios: DominioDeLaWeb[]
}

/** El dominio de la plataforma. Lo pone la construcción del panel; por omisión, el de producción. */
export const DOMINIO_PLATAFORMA = ((import.meta.env.VITE_DOMINIO_PLATAFORMA as string | undefined) || 'wixy.app').trim().toLowerCase()

/** La dirección que toda web tiene aunque no tenga dominio propio: `<slug>.wixy.app`. */
export const direccionEnWixy = (slug: string): string => `${slug}.${DOMINIO_PLATAFORMA}`

/** El dominio con el que se enseña una web: el principal suyo o, si no tiene, el de Wixy. Nunca vacío. */
export const dominioVisible = (sitio: Pick<Sitio, 'direccionDeAgencia' | 'direccionDeAgenciaLista' | 'dominios' | 'slug'>): string =>
  sitio.dominios?.[0]?.dominio || (sitio.direccionDeAgenciaLista && sitio.direccionDeAgencia) || direccionEnWixy(sitio.slug)

const pendiente = (d: DominioDeLaWeb) => d.estado === 'esperando-dns' || d.estado === 'conectando' || d.estado === 'certificado'

export const useDominios = (sitio: number, habilitado = true) =>
  useQuery({
    queryKey: ['dominios', sitio],
    enabled: habilitado,
    queryFn: () => pedir<DominiosDeLaWeb>(`/panel/dominios${consulta({ sitio })}`),
    // Mientras algo esté por conectar, se vuelve a preguntar: el núcleo decide si toca mirar el DNS.
    refetchInterval: (q) => (q.state.data?.dominios.some(pendiente) ? 30_000 : false),
    retry: (veces, error) => veces < 2 && !(error as { status?: number }).status,
  })
